import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Content, FilledContentRelationshipField, FilledLinkToWebField, LinkField } from '@prismicio/client';
import { ProductCatalogService, RequestParams } from '@yol-digital/ms-client';
import { EMPTY, forkJoin, lastValueFrom, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, timeout } from 'rxjs/operators';
import { ENVIRONMENT_URLS_CONFIG_TOKEN } from 'app-initialization';
import { FeatureFlagService } from 'feature-flag';
import { LanguageService } from 'language';
import { CmsService, PrismicLibService, ProductBoxDocument } from 'prismic';
import { Promotion, PromotionService } from 'promotion-shared';
import { ToastService } from 'toast';
import { TranslateService } from 'translate';
import { CachedDataLoaderService, ObservableCachedDataLoaderService } from 'utils';
import { ProductListItem } from './interfaces/product-list-item.interface';
import { Product } from './interfaces/product.class';
import Api = ProductCatalogService.Api;

type CatalogResponse = ProductCatalogService.CatalogResponse;
type RatePlanResponse = ProductCatalogService.RatePlanResponse;

export interface PrismicSelfcareProducts {
  product: { data: { product_code: string } };
  promotion: { data: { rfe_promotion_code: string; zira_promotion_code: string } };
}

export abstract class ProductService {
  protected api: Api;
  protected queryParams: Params;
  protected languageService = inject(LanguageService);
  protected dataLoader = inject(CachedDataLoaderService);
  protected observableDataLoader = inject(ObservableCachedDataLoaderService);
  protected http = inject(HttpClient);
  protected toasts = inject(ToastService);
  protected translateService = inject(TranslateService);
  protected route = inject(ActivatedRoute);
  protected promotionService = inject(PromotionService);
  protected config = inject(ENVIRONMENT_URLS_CONFIG_TOKEN);
  protected platformId = inject(PLATFORM_ID);
  protected prismicLibService = inject(PrismicLibService);
  protected featureFlagService = inject(FeatureFlagService);
  protected cmsService = inject(CmsService);

  constructor() {
    this.api = new Api(this.config.newMicroServiceEndpoint, this.http);

    this.route.queryParams.subscribe(params => {
      this.queryParams = params;
    });
  }

  protected fetchCatalog() {
    return this.fetchPC<CatalogResponse[]>(() => this.getCatalog());
  }

  protected fetchRatePlans() {
    return this.fetchPC<RatePlanResponse[]>(() => this._getRatePlans());
  }

  private fetchPC<T>(fetchFn: () => Observable<{ plans: T } | { error: string }>): Observable<T> {
    let request$ = fetchFn();

    if (isPlatformServer(this.platformId)) {
      request$ = request$.pipe(timeout(5000));
    }

    return request$.pipe(
      catchError(err => {
        if (!err?.wasCaught) {
          console.error('Error while retrieving plans', err);
          this.toasts.add(this.translateService.getTranslation(['server_error']), false);
        }
        return EMPTY;
      }),
      map((response: { plans: T }) => response.plans),
      shareReplay(1)
    );
  }

  public getProductName(data?: Content.ProductBoxDocumentData): string {
    return data.display_name ?? data.product_name.replace(/yallo|lebara|chmobile/g, '');
  }

  protected abstract getCatalog(
    query?: { code?: string },
    params?: RequestParams
  ): Observable<{ plans: CatalogResponse[] } | { error: string }>;

  protected abstract _getRatePlans(
    params?: RequestParams
  ): Observable<{ plans: RatePlanResponse[] } | { error: string }>;

  public async getPCProducts(): Promise<CatalogResponse[]> {
    return this.dataLoader.get('PC_products', () => lastValueFrom(this.fetchCatalog()).catch((): null => null));
  }

  public async getRatePlans(): Promise<RatePlanResponse[]> {
    return this.dataLoader.get('PC_rateplans', () => lastValueFrom(this.fetchRatePlans()).catch((): null => null));
  }

  public abstract getPCProducts$(): Observable<CatalogResponse[]>;

  public abstract getPCProductByCode(code: string): Observable<CatalogResponse>;

  public abstract getRateplanByCode(code: string): Observable<RatePlanResponse>;

  public sortProductsByDiscount(a: ProductListItem, b: ProductListItem): number {
    const a_price = a.promotion ? a.product.discountedPrice(a.promotion?.discountAmount) : a.product.monthly_cost;
    const b_price = b.promotion ? b.product.discountedPrice(b.promotion?.discountAmount) : b.product.monthly_cost;
    if (Number(a_price) < Number(b_price)) {
      return -1;
    }
    if (Number(a_price) > Number(b_price)) {
      return 1;
    }
    return 0;
  }

  protected buildCheckoutQueryParams(
    product: Product,
    promotion?: Promotion,
    lineCheckId?: string,
    extraParams?: { [key: string]: string },
    isLegacy = false
  ): { [key: string]: string } {
    const queryParamsObj: { [key: string]: string } = {};

    const effectiveLockingPeriod = Math.min(
      ...(promotion?.lockinLengths?.length ? promotion.lockinLengths : product.lockin_length || [])
    );

    if (!isNaN(effectiveLockingPeriod)) {
      const lockingPeriodKey = isLegacy ? 'll' : 'lockingPeriod';
      queryParamsObj[lockingPeriodKey] = effectiveLockingPeriod.toString();
    }

    if (promotion?.code) {
      const promotionKey = isLegacy ? 'promotions' : 'promotion';
      queryParamsObj[promotionKey] = promotion.code;
    }

    if (lineCheckId) {
      queryParamsObj.lineCheckId = lineCheckId;
    }

    const { utm_medium, utm_source, utm_campaign } = this.queryParams;
    if (utm_medium && utm_source && utm_campaign) {
      queryParamsObj.utm_medium = utm_medium;
      queryParamsObj.utm_source = utm_source;
      queryParamsObj.utm_campaign = utm_campaign;
    }

    if (extraParams) {
      Object.entries(extraParams).forEach(([key, value]) => (queryParamsObj[key] = value));
    }

    return queryParamsObj;
  }

  public abstract buildCheckoutPath(
    product: Product,
    promotion?: Promotion,
    lineCheckId?: string,
    extraParams?: { [key: string]: string }
  ): { link: LinkField; queryParams?: object };

  public isLinkToCheckout(linkField: LinkField) {
    return (
      (linkField.link_type === 'Document' && (linkField as FilledContentRelationshipField).slug?.includes('eshop')) ||
      (linkField.link_type === 'Web' && (linkField as FilledLinkToWebField).url.search(this.config.checkoutUrl) !== -1)
    );
  }

  public async convertProductBox(
    prismicDocument: ProductBoxDocument,
    promotionFromPrismic?: FilledContentRelationshipField
  ): Promise<ProductListItem> {
    const productsFromPC = await this.getPCProducts();
    if (!productsFromPC) return null;
    const productFromMs = productsFromPC.find(
      (product: ProductCatalogService.CatalogResponse) => product.code === prismicDocument?.data?.product_code
    );

    if (!productFromMs) return null;
    const product = this.createNewProduct(productFromMs, prismicDocument.data);

    const promotion = await this.getCorrectPromotion(promotionFromPrismic, productFromMs);

    return {
      product,
      promotion,
    };
  }

  public async getCorrectPromotion(
    promotionFromPrismic?: FilledContentRelationshipField,
    productFromMs?: ProductCatalogService.CatalogResponse,
    promotionCodeFromOfferCtaLink?: string
  ) {
    // If theres promotion coming from landing page, overwrite the promotion from CTA link (if any - only offer) and the default from PC
    const promotionIdFromPrismic = promotionFromPrismic?.id;
    const productSpecClass = productFromMs?.productSpecClass;

    let promotion: Promotion;
    if (promotionIdFromPrismic) {
      const _promotion = await this.promotionService.getPromotionFromPrismicById(
        promotionFromPrismic.id,
        productSpecClass
      );
      if (_promotion) promotion = _promotion;
      else console.warn('Promotion linked to product is not valid', promotionFromPrismic, productFromMs.displayName);
    } else if (promotionCodeFromOfferCtaLink) {
      // An offer can have a cta slice with a button link text that can contain promotion code
      const _promotion = await this.promotionService.getPromotionFromPrismicByCode(
        promotionCodeFromOfferCtaLink,
        productSpecClass
      );
      if (_promotion) promotion = _promotion;
      else console.warn('Promotion linked to offer is not valid', productFromMs?.displayName);
    }

    if (!promotion) {
      //default promo from PC
      if (productFromMs && productFromMs.defaultPromotion) {
        const _promotion = await this.promotionService.getPromotionFromPrismicByCode(
          productFromMs.defaultPromotion.code,
          productSpecClass
        );
        if (_promotion) promotion = _promotion;
      }
    }
    return promotion;
  }

  public isLineCheckHidden() {
    return false;
  }

  public productDetailsLink(product: Product): LinkField {
    if (!product.product_details_link || product.product_details_link.link_type === 'Any') {
      return <LinkField>{
        link_type: 'Document',
        slug: `mobile-products/${product.url}`,
      };
    }
    return product.product_details_link;
  }

  public getProductBoxList(customProductsFromPrismic = false): Observable<ProductListItem[]> {
    const customProductsFromPrismicCall$ = customProductsFromPrismic ? this.customProductsFromPrismic() : of(null);

    return forkJoin([
      this.getPCProducts$(),
      customProductsFromPrismicCall$,
      this.prismicLibService.getProductBoxes(),
    ]).pipe(
      switchMap(([productCatalogList, prismicProductsAndPromotions, allProductBoxes]) => {
        const productCatalogFilteredList = productCatalogList.filter(
          product =>
            !customProductsFromPrismic ||
            !prismicProductsAndPromotions ||
            prismicProductsAndPromotions.find(item => item.productCode === product.code)
        );
        return forkJoin(
          productCatalogFilteredList.map(product => {
            const productBoxFromPrismic = allProductBoxes.find(
              productBox => productBox.data.product_code === product.code
            )?.data as unknown as Content.ProductBoxDocumentData;
            const customPromotionCode = prismicProductsAndPromotions?.find(
              item => item.productCode === product.code
            )?.promotionCode;

            return forkJoin({
              product: of(this.createNewProduct(product, productBoxFromPrismic)),
              promotion: customPromotionCode
                ? this.promotionService.getPromotionFromPrismicByCode(customPromotionCode, product.productSpecClass)
                : of(product.defaultPromotion ? new Promotion(product.defaultPromotion) : undefined),
            });
          })
        );
      })
    );
  }

  protected createNewProduct(
    product: ProductCatalogService.CatalogResponse,
    prismicData: Content.ProductBoxDocumentData
  ) {
    return new Product(product, prismicData);
  }

  public abstract customProductsFromPrismic(): Observable<{ productCode: string; promotionCode: string }[]>;

  public getDiscountPercentage = (monthly_cost: number, discountAmount: number) =>
    Promotion.discountPercentage(Number(monthly_cost), discountAmount);
  public contractDuration = (promoLockinLengths: number[] = [], productLockingLengths: number[] = []): number => {
    if (promoLockinLengths.length) {
      return Math.min(...promoLockinLengths);
    }

    if (productLockingLengths.length) {
      return Math.min(...productLockingLengths);
    }

    return 0;
  };

  public getChipsLabelsKeys(
    hasPromotion: boolean,
    hasDiscountedCost: boolean,
    activationFee: number,
    contractDuration: number
  ) {
    const chipsKeys = [];
    if (hasPromotion && hasDiscountedCost) {
      chipsKeys.push(this.translateService.getTranslation(['discountForever']));
    }

    if (activationFee === 0) {
      chipsKeys.push(this.translateService.getTranslation(['freeActivation']));
    }

    if (contractDuration === 0) {
      chipsKeys.push(this.translateService.getTranslation(['noContractDuration']));
    }

    return chipsKeys;
  }

  public abstract getAllPromotionsForProductAndChannel(
    productCode: string,
    channel: string
  ): Observable<
    | {
        promotions: ProductCatalogService.PosPromotionResponse[];
      }
    | {
        error: string;
        message?: string;
      }
  >;
}
